import { forwardRef, InputHTMLAttributes, ReactElement, useEffect, useState } from 'react';

// Utilities
import classnames from 'classnames';

// Style
import style from './Style.module.scss';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    error?: boolean;
    errorMessage?: any;
    errorClassName?: string;

    className?: string;
    inputClassName?: string;
    inputFocusClassName?: string;
    inputErrorClassName?: string;

    labelClassName?: string;
    labelFloatClassName?: string;

    requiredMark?: boolean;
    label?: string | ReactElement;
}

const Input = forwardRef<HTMLInputElement, InputProps & InputHTMLAttributes<HTMLInputElement>>(
    (
        {
            label,
            error,
            className,
            errorMessage,
            requiredMark,
            errorClassName,
            labelClassName,
            inputClassName,
            labelFloatClassName,
            inputFocusClassName,
            inputErrorClassName,
            ...props
        },
        ref,
    ) => {
        const [type, setType] = useState<string>(props.type);
        const [value, setValue] = useState<any>(props.value);
        const [isFocused, setIsFocused] = useState<boolean>(false);

        useEffect(() => {
            if (props.value !== value) {
                setValue(props.value);
            }
        }, [props.value]);

        const handleInputFocus = (event) => {
            setIsFocused(true);
            if (props.onFocus) {
                props.onFocus(event);
            }
        };

        const handleInputBlur = (event) => {
            setIsFocused(false);
            if (props.onBlur) {
                props.onBlur(event);
            }
        };

        const handleInputChange = (event) => {
            setValue(event.target.value);
            if (props.onChange) {
                props.onChange(event);
            }
        };

        const handleTogglePassword = () => {
            setType(type === 'password' ? 'text' : 'password');
        };

        const combinedWrapperClassName = classnames([
            style.wrapper,
            className,
            {
                [style.float]: !!value || isFocused,
                [labelFloatClassName]: !!labelFloatClassName && (!!value || isFocused),
            },
        ]);
        const combinedInputClassName = classnames([
            style.input,
            inputClassName,
            {
                [style.error]: error,
                [style.focus]: isFocused,
                [style.password]: props.type === 'password',

                [inputFocusClassName]: !!inputFocusClassName && isFocused,

                [inputErrorClassName]: !!inputErrorClassName && error,
            },
        ]);
        const combinedLabelClassName = classnames([style.label, labelClassName, { [style.required]: requiredMark }]);

        return (
            <>
                <div className={combinedWrapperClassName}>
                    <label className={combinedLabelClassName}>{label}</label>

                    {props.type === 'password' && (
                        <a className={classnames(style.password_toggle, style[type])} onClick={handleTogglePassword} />
                    )}

                    <input
                        ref={ref}
                        {...props}
                        type={type}
                        value={value}
                        autoComplete="off"
                        onBlur={handleInputBlur}
                        onFocus={handleInputFocus}
                        onChange={handleInputChange}
                        className={combinedInputClassName}
                    />
                </div>
                {errorMessage && (
                    <div className={classnames([style.field_message_error, errorClassName])}>{errorMessage}</div>
                )}
            </>
        );
    },
);

export default Input;
